import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { marked } from "marked";
import { api } from "../../utils/api.js";
import { useAuthStore } from "../../store/auth.js";
import { ApplicationFieldEditor } from "./ApplicationFieldEditor.js";
import { JobAuditLog } from "./JobAuditLog.js";

interface ApplicationField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  options?: string[];
}

interface JobData {
  slug?: string;
  title: string;
  team: string;
  location: string;
  status: string;
  hiringManager: string;
  slackChannel: string;
  notes: string;
  assignmentId: number | null;
  applicationFields: ApplicationField[];
  descriptionMarkdown: string;
}

interface Employee {
  email: string;
  displayName: string;
}

interface AssignmentOption {
  id: number;
  title: string;
}

interface EvalCriteriaOption {
  id: number;
  title: string;
}

const TEAMS = [
  "System Engineering",
  "Network Engineering",
  "Platform Engineering",
  "Data Center Engineering",
  "Strategy",
];

const EMPTY_JOB: JobData = {
  title: "",
  team: "",
  location: "Remote",
  status: "draft",
  hiringManager: "",
  slackChannel: "",
  notes: "",
  assignmentId: null,
  applicationFields: [],
  descriptionMarkdown: "",
};

export function JobForm() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isEdit = Boolean(slug);

  const [job, setJob] = useState<JobData>({
    ...EMPTY_JOB,
    hiringManager: user?.email ?? "",
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [assignmentOptions, setAssignmentOptions] = useState<AssignmentOption[]>([]);
  const [evalCriteriaOptions, setEvalCriteriaOptions] = useState<EvalCriteriaOption[]>([]);
  const [evalCriteriaId, setEvalCriteriaId] = useState<number | null>(null);
  const [evalMappingSaving, setEvalMappingSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");

  const savedSnapshot = useRef<string>("");
  const isDirty = JSON.stringify(job) !== savedSnapshot.current;

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const guardedNavigate = useCallback(
    (to: string) => {
      if (isDirty && !window.confirm("You have unsaved changes. Leave this page?")) return;
      navigate(to);
    },
    [isDirty, navigate],
  );

  useEffect(() => {
    api
      .get<{ results: Employee[] }>("/job-admin/employees")
      .then((d) => setEmployees(d.results))
      .catch(() => {});
    api
      .get<{ results: AssignmentOption[] }>("/assignment-library")
      .then((d) => setAssignmentOptions(d.results))
      .catch(() => {});
    api
      .get<{ results: EvalCriteriaOption[] }>("/eval-criteria")
      .then((d) => setEvalCriteriaOptions(d.results))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!slug) {
      savedSnapshot.current = JSON.stringify({
        ...EMPTY_JOB,
        hiringManager: user?.email ?? "",
      });
      return;
    }
    api
      .get<JobData>(`/job-admin/${slug}`)
      .then((d) => {
        setJob(d);
        savedSnapshot.current = JSON.stringify(d);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load job"))
      .finally(() => setLoading(false));
    api
      .get<{ results: { jobSlug: string; evaluationCriteriaId: number }[] }>(
        "/eval-criteria/mappings",
      )
      .then((d) => {
        const mapping = d.results.find((m) => m.jobSlug === slug);
        if (mapping) setEvalCriteriaId(mapping.evaluationCriteriaId);
      })
      .catch(() => {});
  }, [slug, user?.email]);

  const updateField = useCallback(<K extends keyof JobData>(key: K, value: JobData[K]) => {
    setJob((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDocxUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await fetch("/api/v1/job-admin/parse-docx", {
        method: "POST",
        body: formData,
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text);
      }
      const data = (await resp.json()) as {
        markdown: string;
        title?: string;
        team?: string;
        location?: string;
      };
      setJob((prev) => ({
        ...prev,
        descriptionMarkdown: data.markdown,
        ...(data.title && !prev.title ? { title: data.title } : {}),
        ...(data.team && !prev.team ? { team: data.team } : {}),
        ...(data.location && !prev.location.trim()
          ? { location: data.location }
          : data.location && prev.location === "Remote"
            ? { location: data.location }
            : {}),
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to parse .docx");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/job-admin/${slug}`, job);
        savedSnapshot.current = JSON.stringify(job);
        navigate("/jobs/manage?success=Job+saved");
      } else {
        await api.post<{ slug: string }>("/job-admin", job);
        savedSnapshot.current = JSON.stringify(job);
        navigate("/jobs/manage?success=Job+created");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save job");
    } finally {
      setSaving(false);
    }
  };

  const togglePreview = () => {
    if (!previewMode) {
      const html = marked.parse(job.descriptionMarkdown, { async: false }) as string;
      setPreviewHtml(html);
    }
    setPreviewMode(!previewMode);
  };

  const [copied, setCopied] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  if (loading) {
    return <div className="text-gray-400 animate-pulse">Loading job...</div>;
  }

  const hiringManagerInList = employees.some((e) => e.email === job.hiringManager);

  const publicBaseUrl = window.location.hostname.includes("internal")
    ? "https://talent.mntl.it"
    : window.location.origin;
  const jobPublicUrl = slug ? `${publicBaseUrl}/jobs/${slug}` : null;

  const copyUrl = () => {
    if (!jobPublicUrl) return;
    navigator.clipboard.writeText(jobPublicUrl).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => window.prompt("Copy this URL:", jobPublicUrl),
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Link
            to="/jobs/manage"
            className="text-sm text-primary-700 hover:text-primary-800 mb-2 block"
          >
            &larr; All Jobs
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{isEdit ? "Edit Job" : "New Job"}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isEdit ? `Editing ${job.title || slug}` : "Create a new job posting"}
          </p>
        </div>
        {isEdit && slug && (
          <button
            onClick={() => setAuditOpen(!auditOpen)}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 border border-gray-200 rounded-md px-3 py-1.5 transition-colors"
          >
            Activity Log
          </button>
        )}
      </div>

      {isEdit && jobPublicUrl && job.status === "published" && (
        <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-4 py-2.5">
          <span className="text-xs font-medium text-gray-500 shrink-0">Applicant URL</span>
          <code className="flex-1 truncate text-xs text-gray-700">{jobPublicUrl}</code>
          <button
            onClick={copyUrl}
            className="shrink-0 rounded bg-white border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      )}

      {error && <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="space-y-5 rounded-lg border border-gray-200 bg-white p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Title" required>
            <input
              type="text"
              value={job.title}
              onChange={(e) => updateField("title", e.target.value)}
              placeholder="Software Engineer, AI Infrastructure"
              className="input"
            />
          </Field>
          <Field label="Team" required>
            <select
              value={job.team}
              onChange={(e) => updateField("team", e.target.value)}
              className="input"
            >
              <option value="">Select team...</option>
              {TEAMS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Location">
            <select
              value={job.location}
              onChange={(e) => updateField("location", e.target.value)}
              className="input"
            >
              <option value="Remote">Remote</option>
              <option value="Hybrid">Hybrid</option>
              <option value="Local">Local</option>
            </select>
          </Field>
          <Field label="Status">
            <select
              value={job.status}
              onChange={(e) => updateField("status", e.target.value)}
              className="input"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="closed">Closed</option>
            </select>
          </Field>
          <Field label="Hiring Manager" required>
            <select
              value={hiringManagerInList ? job.hiringManager : "__custom__"}
              onChange={(e) => {
                if (e.target.value !== "__custom__") {
                  updateField("hiringManager", e.target.value);
                }
              }}
              className="input"
            >
              <option value="">Select employee...</option>
              {employees.map((emp) => (
                <option key={emp.email} value={emp.email}>
                  {emp.displayName} ({emp.email})
                </option>
              ))}
              {!hiringManagerInList && job.hiringManager && (
                <option value="__custom__">{job.hiringManager}</option>
              )}
            </select>
          </Field>
          <Field label="Slack URL">
            <input
              type="url"
              value={job.slackChannel}
              onChange={(e) => updateField("slackChannel", e.target.value)}
              placeholder="https://neurondc.slack.com/archives/..."
              className="input"
            />
          </Field>
          <Field label="Assignment">
            <select
              value={job.assignmentId ?? ""}
              onChange={(e) =>
                updateField("assignmentId", e.target.value ? Number(e.target.value) : null)
              }
              className="input"
            >
              <option value="">None</option>
              {assignmentOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.title}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Evaluation Criteria">
            <div className="flex items-center gap-2">
              <select
                value={evalCriteriaId ?? ""}
                onChange={(e) => {
                  const newId = e.target.value ? Number(e.target.value) : null;
                  setEvalCriteriaId(newId);
                  if (!slug) return;
                  setEvalMappingSaving(true);
                  const action = newId
                    ? api.post(`/eval-criteria/${newId}/map-job`, { jobSlug: slug })
                    : evalCriteriaId
                      ? api.delete(`/eval-criteria/${evalCriteriaId}/map-job/${slug}`)
                      : Promise.resolve();
                  action
                    .catch((err) =>
                      setError(err instanceof Error ? err.message : "Failed to update mapping"),
                    )
                    .finally(() => setEvalMappingSaving(false));
                }}
                className="input"
                disabled={!slug}
              >
                <option value="">None</option>
                {evalCriteriaOptions.map((ec) => (
                  <option key={ec.id} value={ec.id}>
                    {ec.title}
                  </option>
                ))}
              </select>
              {evalMappingSaving && (
                <span className="text-xs text-gray-400 animate-pulse">Saving...</span>
              )}
            </div>
            {!slug && (
              <p className="text-xs text-gray-400 mt-1">
                Save the job first to set evaluation criteria
              </p>
            )}
          </Field>
        </div>

        <Field label="Internal Notes">
          <textarea
            value={job.notes}
            onChange={(e) => updateField("notes", e.target.value)}
            rows={3}
            placeholder="Internal notes for hiring team (not visible to applicants)"
            className="input"
          />
          <p className="text-xs text-gray-400 mt-1">Employee-only, not visible to applicants</p>
        </Field>

        {/* Description with Edit/Preview toggle */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-3">
              <label className="block text-sm font-medium text-gray-700">
                Job Description <span className="text-red-500">*</span>
              </label>
              <div className="flex rounded-md border border-gray-200 text-xs overflow-hidden">
                <button
                  type="button"
                  onClick={() => setPreviewMode(false)}
                  className={`px-2.5 py-1 transition-colors ${!previewMode ? "bg-gray-100 font-medium text-gray-800" : "text-gray-500 hover:bg-gray-50"}`}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={togglePreview}
                  className={`px-2.5 py-1 transition-colors ${previewMode ? "bg-gray-100 font-medium text-gray-800" : "text-gray-500 hover:bg-gray-50"}`}
                >
                  Preview
                </button>
              </div>
            </div>
            <label className="relative cursor-pointer rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 transition-colors">
              {uploading ? "Parsing..." : "Upload .docx"}
              <input
                type="file"
                accept=".docx"
                onChange={handleDocxUpload}
                disabled={uploading}
                className="sr-only"
              />
            </label>
          </div>
          {previewMode ? (
            <div
              className="prose prose-sm max-w-none rounded-md border border-gray-300 bg-white p-4 min-h-[24rem] overflow-auto"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : (
            <textarea
              value={job.descriptionMarkdown}
              onChange={(e) => updateField("descriptionMarkdown", e.target.value)}
              rows={16}
              placeholder="Paste or upload the job description in markdown format..."
              className="input font-mono text-xs leading-relaxed"
            />
          )}
          <p className="mt-1 text-xs text-gray-400">
            Markdown format. Upload a .docx file to auto-convert, or paste directly.
          </p>
        </div>

        <ApplicationFieldEditor
          fields={job.applicationFields}
          onChange={(fields) => updateField("applicationFields", fields)}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => guardedNavigate("/jobs/manage")}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
        <div className="flex items-center gap-3">
          {isDirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
          <button
            onClick={handleSave}
            disabled={
              saving ||
              !job.title.trim() ||
              !job.team.trim() ||
              !job.descriptionMarkdown.trim() ||
              !job.hiringManager.trim()
            }
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-[var(--action-primary-hover)] disabled:opacity-50 transition-colors"
          >
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Job"}
          </button>
        </div>
      </div>

      {isEdit && slug && (
        <JobAuditLog slug={slug} open={auditOpen} onClose={() => setAuditOpen(false)} />
      )}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}
